import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiCode, FiShoppingBag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  ContentManagerTabBar,
  InfoSection,
  Page,
  RamblaActionButtonContent,
  Table,
} from 'components';
import { useDeleteWidget, useFetchWidgets } from 'api/widgets/hooks';
import columns from 'constants/columns';
import { WIDGET_CREATE, WIDGET_SHOPIFY_GUIDE } from 'constants/routes';
import { media } from 'utils/rwd';

const ShopifyInfoBody = styled(InfoSection.Body)`
  grid-template-columns: minmax(0, 1fr) minmax(180px, 220px);
  align-items: center;
  overflow: hidden;

  ${media.sm.down`
    grid-template-columns: 1fr;
  `}
`;

const ShopifyIntro = styled.div`
  min-width: 0;
`;

const ShopifyDescription = styled.p`
  max-width: 720px;
  margin: 0 0 20px;
  color: #495057;
`;

const WidgetOptionActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  margin-top: 8px;
  padding: 2px 8px;
  border: 1px solid #be1622;
  border-radius: 4px;
  color: #fff;
  background: #be1622;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover,
  &:focus {
    border-color: #a6121d;
    background: #a6121d;
  }

  &:focus-visible {
    box-shadow: 0 0 0 0.15rem #edc5c7;
  }
`;

const WidgetOptionList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const WidgetOptionItem = styled.li`
  display: flex;
  gap: 12px;
`;

const WidgetOptionIcon = styled.span`
  width: 32px;
  height: 32px;
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  color: #be1622;
  background-color: #f8e8e9;
  border-radius: 8px;
`;

const WidgetOptionContent = styled.div`
  min-width: 0;
`;

const WidgetOptionTitle = styled.span`
  display: block;
  margin-bottom: 4px;
  color: #000;
  font-weight: 600;
`;

const WidgetOptionDescription = styled.span`
  display: block;
  color: #6c757d;
  font-size: 14px;
`;

const WidgetsPage = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const navigate = useNavigate();

  const { data: widgets = [], isLoading } = useFetchWidgets();
  const { mutate: deleteWidget } = useDeleteWidget();
  const shopifyWidgetOptions = [
    {
      Icon: FiShoppingBag,
      title: t('shopifyWidgetsStoreTitle'),
      description: t('shopifyWidgetsStoreDescription'),
      actionLabel: t('shopifyWidgetsGuideButton'),
      onAction: () => navigate(WIDGET_SHOPIFY_GUIDE),
    },
    {
      Icon: FiCode,
      title: t('shopifyWidgetsEmbedTitle'),
      description: t('shopifyWidgetsEmbedDescription'),
    },
  ];

  const handleDelete = (widget) => {
    deleteWidget(widget.uid);
  };

  return (
    <Page title={t('listTitle')} description={t('listDescription')} withBackButton={false}>
      <ContentManagerTabBar />
      <Container fluid className="mt-5">
        <Row>
          <Col>
            <InfoSection>
              <InfoSection.Header title={t('shopifyWidgetsTitle')} />
              <ShopifyInfoBody>
                <ShopifyIntro>
                  <ShopifyDescription>{t('shopifyWidgetsDescription')}</ShopifyDescription>
                  <WidgetOptionList>
                    {shopifyWidgetOptions.map(
                      ({ Icon, title, description, actionLabel, onAction }) => (
                        <WidgetOptionItem key={title}>
                          <WidgetOptionIcon>
                            <Icon aria-hidden="true" />
                          </WidgetOptionIcon>
                          <WidgetOptionContent>
                            <WidgetOptionTitle>{title}</WidgetOptionTitle>
                            <WidgetOptionDescription>{description}</WidgetOptionDescription>
                            {actionLabel && (
                              <WidgetOptionActionButton type="button" onClick={onAction}>
                                {actionLabel}
                              </WidgetOptionActionButton>
                            )}
                          </WidgetOptionContent>
                        </WidgetOptionItem>
                      ),
                    )}
                  </WidgetOptionList>
                </ShopifyIntro>
              </ShopifyInfoBody>
            </InfoSection>
            <Table loading={isLoading} data={widgets} columns={columns.widgets}>
              <Table.Header title={t('listTableTitle')}>
                <Button onClick={() => navigate(WIDGET_CREATE)} variant="primary">
                  <RamblaActionButtonContent icon="addModule">
                    {t('createNew')}
                  </RamblaActionButtonContent>
                </Button>
              </Table.Header>
              <Table.Filter withGlobalFilter />
              <Table.Content
                deletable
                onDelete={handleDelete}
                noDataMessage={{
                  heading: t('emptyTitle'),
                  description: t('emptySubtitle'),
                  actions: (
                    <Button onClick={() => navigate(WIDGET_CREATE)} variant="primary">
                      <RamblaActionButtonContent icon="addModule">
                        {t('createNew')}
                      </RamblaActionButtonContent>
                    </Button>
                  ),
                }}
              />
            </Table>
          </Col>
        </Row>
      </Container>
    </Page>
  );
};

export default WidgetsPage;
